import { useRef, useState } from "react"
import { Star, Trash2, Plus, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Capacitor } from "@capacitor/core"
import { useQueryClient } from "@tanstack/react-query"
import { Button } from "@/components/ui/button"
import { propertiesService } from "@/features/properties/services/propertiesService"
import { pickImages } from "@/lib/camera"
import type { PropertyPhoto } from "@rentbase/shared"

interface PhotoGridProps {
  propertyId: string
  photos: PropertyPhoto[]
  onChange: (photos: PropertyPhoto[]) => void
}

export function PhotoGrid({ propertyId, photos, onChange }: PhotoGridProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [actionId, setActionId] = useState<string | null>(null)
  const queryClient = useQueryClient()

  async function handleFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    if (!files.length) return
    e.target.value = ""
    await uploadFiles(files)
  }

  async function handleAddPhotoClick() {
    if (Capacitor.isNativePlatform()) {
      const files = await pickImages()
      if (files.length) await uploadFiles(files)
    } else {
      fileInputRef.current?.click()
    }
  }

  async function uploadFiles(files: File[]) {
    setUploading(true)
    try {
      const created = await propertiesService.uploadPhotos(propertyId, files)
      const updated = [...photos, ...created]
      onChange(updated)
      queryClient.invalidateQueries({ queryKey: ["properties", propertyId] })
      toast.success(`${created.length} photo${created.length > 1 ? "s" : ""} uploaded`)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      toast.error(msg ?? "Failed to upload photos")
    } finally {
      setUploading(false)
    }
  }

  async function handleSetCover(photoId: string) {
    setActionId(photoId)
    try {
      await propertiesService.setCoverPhoto(propertyId, photoId)
      onChange(photos.map((p) => ({ ...p, is_cover: p.id === photoId })))
      queryClient.invalidateQueries({ queryKey: ["properties", propertyId] })
    } catch {
      toast.error("Failed to set cover photo")
    } finally {
      setActionId(null)
    }
  }

  async function handleDelete(photoId: string) {
    setActionId(photoId)
    try {
      await propertiesService.deletePhoto(propertyId, photoId)
      const remaining = photos.filter((p) => p.id !== photoId)
      // If we deleted the cover and there are remaining photos, promote the first one locally
      const deletedWasCover = photos.find((p) => p.id === photoId)?.is_cover
      if (deletedWasCover && remaining.length > 0) {
        remaining[0] = { ...remaining[0], is_cover: true }
      }
      onChange(remaining)
      queryClient.invalidateQueries({ queryKey: ["properties", propertyId] })
      toast.success("Photo deleted")
    } catch {
      toast.error("Failed to delete photo")
    } finally {
      setActionId(null)
    }
  }

  const atLimit = photos.length >= 5

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-muted-foreground">{photos.length}/5 photos</span>
        {atLimit && (
          <span className="text-xs text-amber-600 font-medium">Maximum 5 photos per property</span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {photos.map((photo) => (
          <div key={photo.id} className="relative aspect-square rounded-xl overflow-hidden bg-muted">
            <img
              src={photo.url}
              alt=""
              className="w-full h-full object-cover"
            />
            {photo.is_cover && (
              <div className="absolute top-1 left-1 bg-yellow-400 rounded-full p-0.5">
                <Star className="h-3 w-3 text-yellow-900 fill-yellow-900" />
              </div>
            )}
            <div className="absolute bottom-1 right-1 flex gap-1">
              {!photo.is_cover && (
                <button
                  type="button"
                  onClick={() => handleSetCover(photo.id)}
                  disabled={actionId === photo.id}
                  className="bg-black/50 hover:bg-black/70 rounded-full p-1 transition-colors"
                  title="Set as cover"
                >
                  {actionId === photo.id ? (
                    <Loader2 className="h-3 w-3 text-white animate-spin" />
                  ) : (
                    <Star className="h-3 w-3 text-white" />
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={() => handleDelete(photo.id)}
                disabled={actionId === photo.id}
                className="bg-black/50 hover:bg-red-600/80 rounded-full p-1 transition-colors"
                title="Delete photo"
              >
                <Trash2 className="h-3 w-3 text-white" />
              </button>
            </div>
          </div>
        ))}

        {!atLimit && (
          <button
            type="button"
            onClick={handleAddPhotoClick}
            disabled={uploading}
            className="aspect-square rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-primary hover:text-primary transition-colors"
          >
            {uploading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                <Plus className="h-5 w-5" />
                <span className="text-xs">Add</span>
              </>
            )}
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFilesSelected}
      />
    </div>
  )
}

// Pending photo preview for create mode (before property exists)
interface PendingPhotoGridProps {
  files: File[]
  onChange: (files: File[]) => void
}

export function PendingPhotoGrid({ files, onChange }: PendingPhotoGridProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  function handleFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = Array.from(e.target.files ?? [])
    e.target.value = ""
    onChange([...files, ...selected])
  }

  async function handleAddPhotoClick() {
    if (Capacitor.isNativePlatform()) {
      const selected = await pickImages()
      if (selected.length) onChange([...files, ...selected])
    } else {
      fileInputRef.current?.click()
    }
  }

  function removeFile(index: number) {
    onChange(files.filter((_, i) => i !== index))
  }

  return (
    <div>
      <div className="grid grid-cols-3 gap-2">
        {files.map((file, i) => (
          <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-muted">
            <img
              src={URL.createObjectURL(file)}
              alt=""
              className="w-full h-full object-cover"
            />
            {i === 0 && (
              <div className="absolute top-1 left-1 bg-yellow-400 rounded-full p-0.5">
                <Star className="h-3 w-3 text-yellow-900 fill-yellow-900" />
              </div>
            )}
            <button
              type="button"
              onClick={() => removeFile(i)}
              className="absolute bottom-1 right-1 bg-black/50 hover:bg-red-600/80 rounded-full p-1 transition-colors"
            >
              <Trash2 className="h-3 w-3 text-white" />
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={handleAddPhotoClick}
          className="aspect-square rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-primary hover:text-primary transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span className="text-xs">Add</span>
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFilesSelected}
      />
    </div>
  )
}

// Document list for both create (pending) and edit (uploaded) modes
interface PendingDocListProps {
  files: File[]
  onChange: (files: File[]) => void
}

export function PendingDocList({ files, onChange }: PendingDocListProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  function handleFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = Array.from(e.target.files ?? [])
    e.target.value = ""
    onChange([...files, ...selected])
  }

  return (
    <div className="space-y-2">
      {files.map((file, i) => (
        <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 text-sm">
          <span className="flex-1 truncate">{file.name}</span>
          <span className="text-xs text-muted-foreground shrink-0">
            {(file.size / 1024).toFixed(0)} KB
          </span>
          <button
            type="button"
            onClick={() => onChange(files.filter((_, idx) => idx !== i))}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        onClick={() => fileInputRef.current?.click()}
      >
        <Plus className="h-4 w-4 mr-1" />
        Attach Document
      </Button>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,image/*"
        multiple
        className="hidden"
        onChange={handleFilesSelected}
      />
    </div>
  )
}

interface UploadedDocListProps {
  propertyId: string
  docs: import("@rentbase/shared").Document[]
  onChange: (docs: import("@rentbase/shared").Document[]) => void
}

export function UploadedDocList({ propertyId, docs, onChange }: UploadedDocListProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()
  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  async function handleFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ""
    if (!files.length) return
    setUploading(true)
    try {
      const uploaded = await Promise.all(
        files.map((f) => propertiesService.uploadDocument(propertyId, f))
      )
      onChange([...docs, ...uploaded])
      queryClient.invalidateQueries({ queryKey: ["plan", "usage"] })
      toast.success(`${uploaded.length} document${uploaded.length > 1 ? "s" : ""} uploaded`)
    } catch {
      toast.error("Failed to upload document")
    } finally {
      setUploading(false)
    }
  }

  async function handleDelete(docId: string) {
    setDeletingId(docId)
    try {
      await propertiesService.deleteDocument(docId)
      onChange(docs.filter((d) => d.id !== docId))
      queryClient.invalidateQueries({ queryKey: ["plan", "usage"] })
    } catch {
      toast.error("Failed to delete document")
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="space-y-2">
      {docs.map((doc) => (
        <div key={doc.id} className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 text-sm">
          <a
            href={doc.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 truncate text-primary hover:underline"
          >
            {doc.file_name ?? "Document"}
          </a>
          {doc.file_size_kb && (
            <span className="text-xs text-muted-foreground shrink-0">{doc.file_size_kb} KB</span>
          )}
          <button
            type="button"
            onClick={() => handleDelete(doc.id)}
            disabled={deletingId === doc.id}
            className="text-muted-foreground hover:text-destructive"
          >
            {deletingId === doc.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </button>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
      >
        {uploading ? (
          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
        ) : (
          <Plus className="h-4 w-4 mr-1" />
        )}
        Attach Document
      </Button>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,image/*"
        multiple
        className="hidden"
        onChange={handleFilesSelected}
      />
    </div>
  )
}
